import axios from "axios";

// Public (non-authenticated) calls used by the meylo.app/{company_slug}/{widget_key}
// standalone agent page. We deliberately use a raw axios instance here so that
// requests do not carry the admin panel's auth token and do not trigger the
// apiClient's 401 refresh/logout flow.
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

const publicClient = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});

export interface PublicAgentCompanyInfo {
  name: string;
  description?: string | null;
  logo_url?: string | null;
  website?: string | null;
  industry?: string | null;
  city?: string | null;
  country?: string | null;
}

export interface PublicAgentPage {
  agent_name: string;
  agent_description?: string | null;
  initial_message: string;
  widget_key: string;
  widget_settings?: any;
  company: PublicAgentCompanyInfo;
  typing_text: string;
  input_placeholder_text: string;
  send_button_text: string;
}

export interface PublicChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface PublicChatHistoryResponse {
  messages: PublicChatMessage[];
  has_more: boolean;
}

export interface PublicChatResponse {
  message: string;
  route?: string;
  is_flow_active?: boolean;
  session_id?: string;
}

const getAgentPage = async (
  companySlug: string,
  widgetKey: string,
  language?: string
): Promise<PublicAgentPage> => {
  const res = await publicClient.get<PublicAgentPage>(
    `/public/chatbot/page/${encodeURIComponent(companySlug)}/${encodeURIComponent(widgetKey)}`,
    language
      ? { headers: { "Accept-Language": language } }
      : undefined
  );
  return res.data;
};

// NOTE: chat & history calls deliberately do NOT pass an Accept-Language
// header. The UI language picker on the public page is only for on-page copy
// (via react-i18next); it must not influence the language the AI speaks in —
// the agent keeps following whatever language the user writes in.
const sendChatMessage = async (
  widgetKey: string,
  message: string,
  sessionId?: string
): Promise<PublicChatResponse> => {
  const res = await publicClient.post<PublicChatResponse>(
    `/public/chatbot/${encodeURIComponent(widgetKey)}/chat`,
    { message, session_id: sessionId }
  );
  return res.data;
};

const getChatHistory = async (
  widgetKey: string,
  sessionId: string,
  params?: { skip?: number; limit?: number }
): Promise<PublicChatHistoryResponse> => {
  const res = await publicClient.get<PublicChatHistoryResponse>(
    `/public/chatbot/${encodeURIComponent(widgetKey)}/history`,
    {
      params: { session_id: sessionId, ...params },
    }
  );
  return res.data;
};

const publicAgentService = {
  getAgentPage,
  sendChatMessage,
  getChatHistory,
};

export default publicAgentService;
